import { useState, useCallback, useRef } from 'react';
import type { TableColumn } from '@/hooks/useTableColumns';
import { useLongPress } from '@/hooks/useLongPress';

const LONG_PRESS_MS = 400;

export interface UseColumnDragDropParams {
  columns: TableColumn[];
  visibleColumns: TableColumn[];
  gridTemplateColumns: string;
  moveColumn: (from: number, to: number) => void;
}

export function useColumnDragDrop({
  columns,
  visibleColumns,
  gridTemplateColumns,
  moveColumn,
}: UseColumnDragDropParams) {
  const touchGhostRef = useRef<HTMLElement | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [draggingColumnIndex, setDraggingColumnIndex] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);
  const dragOverIndexRef = useRef<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, fullIndex: number) => {
    dragIndexRef.current = fullIndex;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(fullIndex));
    const ghost = document.createElement('div');
    ghost.className = 'projects-table__drag-ghost';
    ghost.textContent = columns[fullIndex]?.title ?? '';
    ghost.style.cssText =
      'position:absolute;top:-9999px;padding:8px 12px;background:var(--surface);border:1px solid var(--table-border);border-radius:10px;font-size:11px;font-weight:600;color:var(--text-muted);text-transform:uppercase;white-space:nowrap;box-shadow:var(--shadow-soft);';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2);
    requestAnimationFrame(() => ghost.remove());
    setTimeout(() => setDraggingColumnIndex(fullIndex), 0);
  }, [columns]);

  const handleDragEnd = useCallback((_e: React.DragEvent) => {
    dragIndexRef.current = null;
    setDraggingColumnIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, toFullIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(toFullIndex);
  }, []);

  const handleHeaderRowDragLeave = useCallback((e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    const row = e.currentTarget as HTMLElement;
    if (!related || !row.contains(related)) {
      setDragOverIndex(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, toFullIndex: number) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === null || from === toFullIndex) return;
    moveColumn(from, toFullIndex);
    dragIndexRef.current = null;
    setDraggingColumnIndex(null);
    setDragOverIndex(null);
  }, [moveColumn]);

  const startTouchDrag = useCallback(
    (fullIndex: number, clientX: number, clientY: number) => {
      const ghost = document.createElement('div');
      ghost.className = 'projects-table__drag-ghost projects-table__touch-ghost';
      ghost.textContent = columns[fullIndex]?.title ?? '';
      ghost.style.cssText =
        'position:fixed;pointer-events:none;z-index:10000;padding:8px 12px;background:var(--surface);border:1px solid var(--table-border);border-radius:10px;font-size:11px;font-weight:600;color:var(--text-muted);text-transform:uppercase;white-space:nowrap;box-shadow:var(--shadow-soft);transform:translate(-50%,-50%);';
      document.body.appendChild(ghost);
      touchGhostRef.current = ghost;
      ghost.style.left = `${clientX}px`;
      ghost.style.top = `${clientY}px`;

      dragIndexRef.current = fullIndex;
      dragOverIndexRef.current = fullIndex;
      setDraggingColumnIndex(fullIndex);
      setDragOverIndex(fullIndex);

      const onTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        const t = e.touches[0];
        if (!t) return;
        ghost.style.left = `${t.clientX}px`;
        ghost.style.top = `${t.clientY}px`;
        const el = document.elementFromPoint(t.clientX, t.clientY);
        const cell = el?.closest('[data-column-index]');
        const idx = cell?.getAttribute('data-column-index');
        if (idx !== null && idx !== undefined) {
          const n = parseInt(idx, 10);
          dragOverIndexRef.current = n;
          setDragOverIndex(n);
        }
      };

      const onTouchEnd = () => {
        const from = dragIndexRef.current;
        const to = dragOverIndexRef.current;
        if (from !== null && to !== null && from !== to) moveColumn(from, to);
        ghost.remove();
        touchGhostRef.current = null;
        dragIndexRef.current = null;
        dragOverIndexRef.current = null;
        setDraggingColumnIndex(null);
        setDragOverIndex(null);
        document.removeEventListener('touchmove', onTouchMove as EventListener, { capture: true });
        document.removeEventListener('touchend', onTouchEnd);
        document.removeEventListener('touchcancel', onTouchEnd);
      };

      document.addEventListener('touchmove', onTouchMove as EventListener, { capture: true, passive: false });
      document.addEventListener('touchend', onTouchEnd);
      document.addEventListener('touchcancel', onTouchEnd);
    },
    [columns, moveColumn]
  );

  const handleLongPressStart = useCallback(
    (e: React.TouchEvent, fullIndex: number) => {
      const t = e.touches[0];
      if (!t) return;
      startTouchDrag(fullIndex, t.clientX, t.clientY);
    },
    [startTouchDrag]
  );

  const { onTouchStart, onTouchEnd, onTouchCancel } = useLongPress(handleLongPressStart, LONG_PRESS_MS);

  const dropIndicatorVisibleIndexRaw =
    dragOverIndex !== null && draggingColumnIndex !== null
      ? visibleColumns.findIndex((c) => columns.findIndex((col) => col.id === c.id) === dragOverIndex)
      : -1;
  const dropIndicatorVisibleIndex = dropIndicatorVisibleIndexRaw >= 0 ? dropIndicatorVisibleIndexRaw : null;

  const gridWithDropIndicator =
    dropIndicatorVisibleIndex !== null
      ? (() => {
          const widths = visibleColumns.map((c) => c.width);
          widths.splice(dropIndicatorVisibleIndex, 0, '6px');
          return widths.join(' ');
        })()
      : gridTemplateColumns;

  return {
    draggingColumnIndex,
    dropIndicatorVisibleIndex,
    gridWithDropIndicator,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleHeaderRowDragLeave,
    handleDrop,
    onTouchStart,
    onTouchEnd,
    onTouchCancel,
  };
}
